/**
 * clipCardPublishState.ts
 *
 * Pure derivation of a Content Studio clip card's published state, unioning the
 * two places a publish can be recorded:
 *
 *   - the Video record's legacy fields (`videoFilePrimary`, `lastPublishJson`),
 *     written only by the canonical cell — original language × that AR ×
 *     highest selected resolution;
 *   - the clip's `PublishRecord`s, which every other cell writes.
 *
 * A clip published only in a translation — translate, dub, publish, the workflow
 * the feature exists for — writes no legacy fields at all, so the card read
 * "Draft" on a clip the user had just published and could already download one
 * tab over (PLAT-11591). Same root cause as PLAT-11548 in the clip preview and
 * PLAT-11485 in share links; this is the grid.
 *
 * The legacy side stays authoritative wherever it answers, so an
 * original-language publish renders exactly what it rendered before — only a
 * translated publish consults records (PLAT-11548 DS2).
 *
 * No IO, no MobX: the caller fetches the records. The video's fields are read
 * through the narrow `ClipPublishStateVideo` shape rather than a whole `Video`
 * so the derivation stays testable with plain objects.
 */

import {
  isPlayableTranslatedRecord,
  pickNewestBestResolution,
} from './publishedRenderSelection';

import type { PublishRecord } from '@src/types.ts/publishRecord';
import type { VideoLastPublish } from '@src/types.ts/story';

/** The per-AR Video node fields this derivation reads (main or associated). */
export interface ClipPublishStateVariant {
  id?: string;
  aspectRatio?: string | null;
  videoFilePrimary?: unknown;
  lastPublishJson?: VideoLastPublish;
}

/** A clip: its own per-AR node plus the sibling nodes for its other ARs. */
export interface ClipPublishStateVideo extends ClipPublishStateVariant {
  associatedVideos?: ClipPublishStateVariant[] | null;
}

/** One aspect-ratio badge on the card. */
export interface ClipFormatPublishState {
  aspectRatio: string;
  /** Live badge + working download, from either source. */
  isPublished: boolean;
  /**
   * The record to download when the legacy field cannot serve this AR — a
   * translated publish. `null` when `videoFilePrimary` holds the render, which
   * keeps the original language on its existing Mux-backed path.
   */
  record: PublishRecord | null;
}

/** The most recent publish, and which language it was, for the card footer. */
export interface ClipLatestPublish {
  info: VideoLastPublish;
  /**
   * The render's language when it is not the story's original, `null` when it
   * is. Not shown on the card footer — that line is too narrow for it, and the
   * aspect-ratio badge carries the language instead — but kept here because it
   * is what identifies *which* version of the clip the latest publish was.
   */
  language: string | null;
}

export interface ClipPublishState {
  formats: ClipFormatPublishState[];
  /** Every publish this clip has, for the grid's filter / sort / creator facet. */
  publishInfo: VideoLastPublish[];
  latest: ClipLatestPublish | null;
}

/**
 * Derive a clip's card state from its variants and their publish records.
 *
 * `recordsFor` is looked up per variant id because every language of one aspect
 * ratio renders into that AR's *own* Video record — so a variant's records are
 * exactly that AR's publishes. `originalLanguage` decides only whether `latest`
 * reports a language: a non-highest-resolution cell of an original-language
 * fan-out is also non-canonical, so "not canonical" does not mean "translated"
 * (the trap PLAT-11548 DS2 documents).
 */
export function deriveClipPublishState(
  video: ClipPublishStateVideo,
  recordsFor: (videoId: string) => PublishRecord[],
  originalLanguage: string,
): ClipPublishState {
  const variants = [video, ...(video.associatedVideos ?? [])];

  const formats: ClipFormatPublishState[] = [];
  const publishInfo: VideoLastPublish[] = [];
  let latest: ClipLatestPublish | null = null;

  const considerLatest = (candidate: ClipLatestPublish) => {
    publishInfo.push(candidate.info);
    if (!latest || isNewer(candidate.info, latest.info)) latest = candidate;
  };

  for (const variant of variants) {
    const aspectRatio = variant.aspectRatio ?? '';
    const records = variant.id ? recordsFor(variant.id) : [];

    // The legacy field answers first, so an original-language publish keeps
    // exactly the badge and footer it has today.
    const legacyPublished = !!variant.videoFilePrimary;
    if (variant.lastPublishJson) {
      considerLatest({ info: variant.lastPublishJson, language: null });
    }

    // A translated render is reachable only through its record. Same predicate
    // and same newest-publish/highest-resolution rule the clip preview plays by.
    const translated = pickNewestBestResolution(
      records.filter(isPlayableTranslatedRecord),
    );
    if (translated) {
      considerLatest({
        info: publishInfoFromRecord(translated),
        language:
          translated.language === originalLanguage ? null : translated.language,
      });
    }

    formats.push({
      aspectRatio,
      isPublished: legacyPublished || !!translated,
      record: legacyPublished ? null : translated,
    });
  }

  return { formats: dedupeByAspectRatio(formats), publishInfo, latest };
}

/**
 * Describe a record the way the render lambda describes a legacy publish, so
 * the footer renders one shape whichever source it came from.
 * `formatResolutionShortLabel` already maps both the enum (`low`) and the
 * legacy pixel string (`270p`), so no conversion is needed here.
 */
function publishInfoFromRecord(record: PublishRecord): VideoLastPublish {
  return {
    // Ranked against legacy timestamps via `new Date(...)`, which parses both
    // this ISO string and the lambda's epoch-ms number.
    timestamp: record.publishedAt ?? 0,
    publisher: record.publishedBy ?? '',
    aspectRatio: record.aspectRatio,
    resolution: record.resolution,
  };
}

function isNewer(candidate: VideoLastPublish, incumbent: VideoLastPublish) {
  return new Date(candidate.timestamp) > new Date(incumbent.timestamp);
}

/**
 * One badge per aspect ratio, first entry winning — the variant order the card
 * already relies on (`getAvailableClipFormats`), which puts the clip's own node
 * ahead of its associated siblings. Sorted so the badges keep a stable order as
 * records land.
 */
function dedupeByAspectRatio(
  formats: ClipFormatPublishState[],
): ClipFormatPublishState[] {
  const byAspectRatio = new Map<string, ClipFormatPublishState>();
  for (const format of formats) {
    if (!byAspectRatio.has(format.aspectRatio)) {
      byAspectRatio.set(format.aspectRatio, format);
    }
  }
  // Plain relational comparison, matching the `orderBy(…, ['aspectRatio'],
  // ['asc'])` this replaces — `localeCompare` orders the ":" in "16:9" against
  // "1:1" differently, which would silently reshuffle the badges.
  return Array.from(byAspectRatio.values()).sort((a, b) =>
    a.aspectRatio < b.aspectRatio ? -1 : a.aspectRatio > b.aspectRatio ? 1 : 0,
  );
}
